import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./PlatformBilling.css";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

function money(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "$0.00";
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function normalizeSubscriptionStatus(v) {
  const s = safeString(v).trim().toUpperCase();
  if (!s) return "NOT_STARTED";
  if (s === "TRIALING") return "TRIAL";
  if (s === "PAST_DUE") return "PAST_DUE";
  if (s === "ACTIVE") return "ACTIVE";
  if (s === "CANCELED") return "CANCELED";
  if (s === "UNPAID") return "PAST_DUE";
  if (s === "INCOMPLETE") return "INCOMPLETE";
  if (s === "EXPIRED" || s === "INCOMPLETE_EXPIRED") return "EXPIRED";
  return s;
}


function normalizePlanNameForDisplay(planName) {
  const raw = safeString(planName).trim();
  const key = raw.toLowerCase().replace(/[\s_-]+/g, " ");

  if (!key) return "";
  if (key === "flowtrak standard" || key === "standard" || key === "flowtrak starter" || key === "starter") {
    return "Flowtrak Starter";
  }
  if (key === "flowtrak high volume" || key === "high volume" || key === "high_volume") {
    return "Flowtrak High Volume";
  }

  return raw;
}

function isPlatformBusinessUnit(bu) {
  const code = safeString(bu?.storeCode || bu?.store_code).trim().toUpperCase();
  const group = safeString(bu?.groupName || bu?.group_name).trim().toUpperCase();
  const store = safeString(bu?.storeName || bu?.store_name).trim().toUpperCase();

  return code === "FLOWTRAK" || (group === "FLOWTRAK" && store === "PLATFORM");
}
function normalizeBU(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    id: String(r?.id ?? r?.business_unit_id ?? ""),
    groupName: safeString(r?.group_name ?? r?.groupName ?? ""),
    storeName: safeString(r?.store_name ?? r?.storeName ?? ""),
    storeCode: safeString(r?.store_code ?? r?.storeCode ?? ""),
    status: safeString(r?.status ?? "ACTIVE").toUpperCase(),
    timezone: safeString(r?.timezone ?? "America/Los_Angeles"),

    subscriptionStatus: normalizeSubscriptionStatus(
      r?.subscriptionStatus ?? r?.subscription_status ?? ""
    ),
    plan: normalizePlanNameForDisplay(r?.plan ?? r?.plan_name ?? ""),
    roCap: Number(r?.roCap ?? r?.ro_cap ?? r?.ro_limit ?? 0) || 0,
    mrr: safeNumber(r?.mrr ?? r?.monthly_price ?? 0, 0),
    renewsAt:
      r?.renewsAt ??
      r?.renews_at ??
      r?.next_renewal_date ??
      r?.current_period_end ??
      null,
    cancelAtPeriodEnd: !!(
      r?.cancelAtPeriodEnd ?? r?.cancel_at_period_end
    ),

    stripeCustomerId: safeString(
      r?.stripeCustomerId ?? r?.stripe_customer_id ?? ""
    ),
    stripeSubscriptionId: safeString(
      r?.stripeSubscriptionId ?? r?.stripe_subscription_id ?? ""
    ),
    stripePriceId: safeString(r?.stripePriceId ?? r?.stripe_price_id ?? ""),
  };
}

function statusTone(status) {
  const s = safeString(status).toUpperCase();
  if (s === "ACTIVE") return "success";
  if (s === "TRIAL") return "info";
  if (s === "PAST_DUE") return "warning";
  if (s === "CANCELED" || s === "EXPIRED" || s === "INCOMPLETE") return "danger";
  return "default";
}

function formatStatus(status) {
  return safeString(status).replace(/_/g, " ");
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function roCapLabel(roCap) {
  const n = Number(roCap);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n.toLocaleString()} ROs/mo`;
}

function Pill({ children, tone = "default" }) {
  return (
    <span className={`ftbi-pill ${tone !== "default" ? `ftbi-pill--${tone}` : ""}`}>
      {children}
    </span>
  );
}

function AttentionRow({ title, body, tone = "neutral" }) {
  return (
    <div className={`ftbi-attentionRow ${tone !== "neutral" ? `is-${tone}` : ""}`}>
      <div className="ftbi-attentionDot" />
      <div className="ftbi-attentionCopy">
        <div className="ftbi-attentionTitle">{title}</div>
        {body ? <div className="ftbi-attentionBody">{body}</div> : null}
      </div>
    </div>
  );
}

function isActiveLikeStatus(status) {
  const s = safeString(status).toUpperCase();
  return s === "ACTIVE" || s === "TRIAL" || s === "PAST_DUE";
}

function formatUnitTitle(bu) {
  if (bu.groupName && bu.storeName) return `${bu.groupName} / ${bu.storeName}`;
  return bu.storeName || bu.groupName || "Unnamed Business Unit";
}

async function copyText(value) {
  const text = safeString(value).trim();
  if (!text) return false;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function PlatformBilling() {
  const nav = useNavigate();
  const loc = useLocation();
  const auth = useAuth();

  const token = safeString(auth?.token).trim();
  const isAuthed = !!token;

  const inPlatformRoute = loc.pathname.startsWith("/platform");
  const platformRole = normalizePlatformRole(
    auth?.user?.platformRole ?? auth?.platformRole ?? ""
  );
  const hasPlatformAccess = !!platformRole;

  const apiGet = auth?.apiGet;
  const apiPost = auth?.apiPost;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [generatedLinksByBU, setGeneratedLinksByBU] = useState({});
  const [copiedKey, setCopiedKey] = useState("");

  const [summary, setSummary] = useState({
    mrr: 0,
    activeSubs: 0,
    trials: 0,
    pastDue: 0,
    businessUnits: [],
  });

  const refresh = useCallback(async () => {
    setErr("");
    setLoading(true);

    try {
      if (!isAuthed) throw new Error("Not signed in.");
      if (!hasPlatformAccess) throw new Error("No Platform access.");
      if (!inPlatformRoute) throw new Error("Platform route required.");
      if (typeof apiGet !== "function") throw new Error("apiGet missing.");

      const data = await apiGet("/api/platform/billing");

      const units = (
        Array.isArray(data?.businessUnits)
          ? data.businessUnits
          : Array.isArray(data?.units)
            ? data.units
            : []
      )
        .map(normalizeBU)
        .filter((bu) => !isPlatformBusinessUnit(bu));

      const next = {
        mrr: data?.mrr ?? data?.summary?.mrr ?? 0,
        activeSubs: data?.activeSubs ?? data?.summary?.activeSubs ?? 0,
        trials: data?.trials ?? data?.summary?.trials ?? 0,
        pastDue: data?.pastDue ?? data?.summary?.pastDue ?? 0,
        businessUnits: units,
      };

      setSummary(next);

      if (expandedId && !units.some((u) => u.id === expandedId)) {
        setExpandedId("");
      }
    } catch (e) {
      setErr(e?.message || "Billing not available yet.");
      setSummary({
        mrr: 0,
        activeSubs: 0,
        trials: 0,
        pastDue: 0,
        businessUnits: [],
      });
      setExpandedId("");
    } finally {
      setLoading(false);
    }
  }, [apiGet, expandedId, hasPlatformAccess, inPlatformRoute, isAuthed]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveGeneratedLink = useCallback((businessUnitId, key, url) => {
    setGeneratedLinksByBU((current) => ({
      ...current,
      [businessUnitId]: {
        ...(current[businessUnitId] || {}),
        [key]: url,
      },
    }));
  }, []);

  const handleCreateCheckoutLink = useCallback(
    async (businessUnitId, planKey) => {
      try {
        setErr("");
        setBusyId(`${businessUnitId}:${planKey}`);

        if (typeof apiPost !== "function") {
          throw new Error("apiPost missing.");
        }

        const data = await apiPost("/api/platform/billing/checkout", {
          businessUnitId,
          planKey,
        });

        const url = safeString(data?.url).trim();
        if (!url) throw new Error("Checkout URL was not returned.");

        saveGeneratedLink(businessUnitId, planKey, url);

        const copied = await copyText(url);
        setCopiedKey(copied ? `${businessUnitId}:${planKey}` : "");

        if (!copied) {
          setErr("Checkout link created, but your browser blocked automatic copy. Use the Copy button.");
        }
      } catch (e) {
        setErr(e?.message || "Failed to create checkout link.");
      } finally {
        setBusyId("");
      }
    },
    [apiPost, saveGeneratedLink]
  );

  const handleCreatePortalLink = useCallback(
    async (businessUnitId) => {
      try {
        setErr("");
        setBusyId(`${businessUnitId}:portal`);

        if (typeof apiPost !== "function") {
          throw new Error("apiPost missing.");
        }

        const data = await apiPost("/api/platform/billing/portal", {
          businessUnitId,
        });

        const url = safeString(data?.url).trim();
        if (!url) throw new Error("Portal URL was not returned.");

        saveGeneratedLink(businessUnitId, "portal", url);

        const copied = await copyText(url);
        setCopiedKey(copied ? `${businessUnitId}:portal` : "");

        if (!copied) {
          setErr("Portal link created, but your browser blocked automatic copy. Use the Copy button.");
        }
      } catch (e) {
        setErr(e?.message || "Failed to create billing portal link.");
      } finally {
        setBusyId("");
      }
    },
    [apiPost, saveGeneratedLink]
  );

  const handleCopySavedLink = useCallback(async (businessUnitId, key) => {
    const url = safeString(generatedLinksByBU?.[businessUnitId]?.[key]).trim();
    if (!url) return;

    const copied = await copyText(url);
    setCopiedKey(copied ? `${businessUnitId}:${key}` : "");
    if (!copied) {
      setErr("Unable to copy automatically. Select the link text and copy it manually.");
    }
  }, [generatedLinksByBU]);

  const handleOpenSavedLink = useCallback((businessUnitId, key) => {
    const url = safeString(generatedLinksByBU?.[businessUnitId]?.[key]).trim();
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [generatedLinksByBU]);

  const buCount = useMemo(
    () => summary.businessUnits?.length || 0,
    [summary.businessUnits]
  );

  const filteredUnits = useMemo(() => {
    const q = safeString(search).trim().toLowerCase();
    if (!q) return summary.businessUnits || [];

    return (summary.businessUnits || []).filter((bu) => {
      const hay = [
        bu.groupName,
        bu.storeName,
        bu.storeCode,
        bu.plan,
        bu.subscriptionStatus,
        formatUnitTitle(bu),
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [search, summary.businessUnits]);

  const attention = useMemo(() => {
    const items = [];

    if ((summary.pastDue || 0) > 0) {
      items.push({
        title: `${summary.pastDue} account${summary.pastDue === 1 ? "" : "s"} past due`,
        body: "Resolve payment issues.",
        tone: "warning",
      });
    }

    if ((summary.trials || 0) > 0) {
      items.push({
        title: `${summary.trials} trial account${summary.trials === 1 ? "" : "s"} active`,
        body: "",
        tone: "info",
      });
    }

    if ((summary.activeSubs || 0) === 0) {
      items.push({
        title: "No paid subscriptions yet",
        body: "Create checkout links and send them to dealership reps.",
        tone: "neutral",
      });
    }

    if (items.length === 0) {
      items.push({
        title: "No immediate billing alerts",
        body: "",
        tone: "success",
      });
    }

    return items;
  }, [summary]);

  useEffect(() => {
    if (!filteredUnits.length && expandedId) {
      setExpandedId("");
      return;
    }

    if (expandedId && !filteredUnits.some((u) => u.id === expandedId)) {
      setExpandedId("");
    }
  }, [filteredUnits, expandedId]);

  if (!isAuthed) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Billing</h1>
            <p className="ft-subtitle">Please sign in to continue.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--primary" type="button" onClick={() => nav("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlatformAccess) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Billing</h1>
            <p className="ft-subtitle">Your account does not have Platform access.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!inPlatformRoute) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Billing</h1>
            <p className="ft-subtitle">
              This page must be accessed under <b>/platform</b>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ft-page ftbi-page">
      <section className="ftbi-hero">
        <div className="ftbi-heroMain">
          <div className="ftbi-eyebrow">Platform Billing</div>
          <h1 className="ft-title">Billing</h1>

          <div className="ftbi-heroMeta">
            <span className="ftbi-heroMetaItem">
              <span className="ftbi-heroMetaLabel">Role</span>
              <span className="ftbi-heroMetaValue">{platformRole.replace(/_/g, " ")}</span>
            </span>
            <span className="ftbi-heroMetaItem">
              <span className="ftbi-heroMetaLabel">Workspace</span>
              <span className="ftbi-heroMetaValue">Platform</span>
            </span>
            <span className="ftbi-heroMetaItem">
              <span className="ftbi-heroMetaLabel">Checkout</span>
              <span className="ftbi-heroMetaValue">Link-based</span>
            </span>
          </div>
        </div>

        <div className="ftbi-heroActions">
          <button className="ft-btn ft-btn--ghost" type="button" onClick={refresh}>
            Refresh
          </button>
        </div>
      </section>

      {err ? (
        <div className="ft-card ft-card--error">
          <div className="ft-errorText">{loading ? "Loading…" : err}</div>
        </div>
      ) : null}

      <section className="ftbi-metricsGrid">
        <div className="ftbi-metricCard ftbi-metricCard--primary">
          <div className="ftbi-metricTop">
            <div className="ftbi-metricLabel">Monthly Recurring Revenue</div>
            <Pill tone="success">Stripe</Pill>
          </div>
          <div className="ftbi-metricValue">{loading ? "—" : money(summary.mrr)}</div>
        </div>

        <div className="ftbi-metricCard">
          <div className="ftbi-metricTop">
            <div className="ftbi-metricLabel">Active Subscriptions</div>
          </div>
          <div className="ftbi-metricValue">{loading ? "—" : summary.activeSubs}</div>
        </div>

        <div className="ftbi-metricCard">
          <div className="ftbi-metricTop">
            <div className="ftbi-metricLabel">Past Due</div>
            {(summary.pastDue || 0) > 0 ? <Pill tone="warning">Attention</Pill> : null}
          </div>
          <div className="ftbi-metricValue">{loading ? "—" : summary.pastDue}</div>
        </div>

        <div className="ftbi-metricCard">
          <div className="ftbi-metricTop">
            <div className="ftbi-metricLabel">Trials</div>
          </div>
          <div className="ftbi-metricValue">{loading ? "—" : summary.trials}</div>
        </div>
      </section>

      <section className="ftbi-mainGrid">
        <aside className="ftbi-sideRail">
          <div className="ftbi-panel ftbi-panel--dark">
            <div className="ftbi-panelHeader">
              <div>
                <div className="ftbi-panelEyebrow">Control</div>
                <div className="ftbi-panelTitle">Attention</div>
              </div>
            </div>

            <div className="ftbi-attentionStack">
              {attention.map((item) => (
                <AttentionRow
                  key={item.title}
                  title={item.title}
                  body={item.body}
                  tone={item.tone}
                />
              ))}
            </div>
          </div>

          <div className="ftbi-panel">
            <div className="ftbi-panelHeader">
              <div>
                <div className="ftbi-panelEyebrow">Portfolio</div>
                <div className="ftbi-panelTitle">Coverage</div>
              </div>
            </div>

            <div className="ftbi-coverageList">
              <div className="ftbi-coverageRow">
                <span className="ftbi-coverageLabel">Business Units</span>
                <span className="ftbi-coverageValue">{loading ? "—" : buCount}</span>
              </div>
              <div className="ftbi-coverageRow">
                <span className="ftbi-coverageLabel">Filtered</span>
                <span className="ftbi-coverageValue">{loading ? "—" : filteredUnits.length}</span>
              </div>
              <div className="ftbi-coverageRow">
                <span className="ftbi-coverageLabel">Billing state</span>
                <span className="ftbi-coverageValue">
                  {(summary.pastDue || 0) > 0 ? "Needs review" : "Stable"}
                </span>
              </div>
            </div>
          </div>
        </aside>

        <section className="ftbi-workspace">
          <div className="ftbi-panel">
            <div className="ftbi-panelHeader ftbi-panelHeader--spaced">
              <div>
                <div className="ftbi-panelEyebrow">Workspace</div>
                <div className="ftbi-panelTitle">Business Units</div>
              </div>
              <div className="ftbi-panelSub">
                {loading ? "Loading…" : `${filteredUnits.length} of ${buCount} shown`}
              </div>
            </div>

            <div className="ftbi-listToolbar">
              <input
                className="ftbi-listSearch"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find by store name or code"
              />
            </div>

            {!loading && filteredUnits.length === 0 ? (
              <div className="ftbi-emptyState">
                <div className="ftbi-emptyTitle">No Business Units match your search</div>
                <div className="ftbi-emptyBody">
                  Try searching by group, store name, store code, plan, or billing status.
                </div>
              </div>
            ) : null}

            <div className="ftbi-compactList">
              {filteredUnits.map((bu) => {
                const isExpanded = expandedId === bu.id;
                const hasStripeCustomer = !!safeString(bu.stripeCustomerId).trim();
                const activeLike = isActiveLikeStatus(bu.subscriptionStatus);
                const onStarter = bu.plan === "Flowtrak Starter";
                const onHighVolume = bu.plan === "Flowtrak High Volume";

                const checkoutStandardBusy = busyId === `${bu.id}:standard`;
                const checkoutHighBusy = busyId === `${bu.id}:high_volume`;
                const portalBusy = busyId === `${bu.id}:portal`;

                const links = generatedLinksByBU[bu.id] || {};
                const starterLink = safeString(links.standard).trim();
                const highVolumeLink = safeString(links.high_volume).trim();
                const portalLink = safeString(links.portal).trim();

                const showStartStandard = !activeLike;
                const showHighVolume = !onHighVolume;
                const anyBusy = checkoutStandardBusy || checkoutHighBusy || portalBusy;

                const rowHint = activeLike
                  ? onHighVolume
                    ? "Create a portal link so the customer can manage card details, invoices, and subscription settings."
                    : "Create a portal link for billing management, or create a High Volume checkout link if the store needs to move up."
                  : "Create a checkout link and send it to the dealership rep. They enter payment details directly in Stripe.";

                return (
                  <div
                    key={bu.id}
                    className={`ftbi-buListItem ${isExpanded ? "is-expanded" : ""}`}
                  >
                    <button
                      type="button"
                      className="ftbi-buSummary"
                      onClick={() => setExpandedId(isExpanded ? "" : bu.id)}
                    >
                      <div className="ftbi-buSummaryMain">
                        <div className="ftbi-buSummaryTitle">{formatUnitTitle(bu)}</div>
                        <div className="ftbi-buSummaryMeta">
                          <span>{bu.storeCode || "No code"}</span>
                        </div>
                      </div>

                      <div className="ftbi-buSummaryCols">
                        <div className="ftbi-buSummaryCol">
                          <div className="ftbi-buSummaryLabel">Status</div>
                          <div className="ftbi-buSummaryValue">
                            <span
                              className={`ftbi-ledgerStatus ftbi-ledgerStatus--${statusTone(
                                bu.subscriptionStatus
                              )}`}
                            >
                              {formatStatus(bu.subscriptionStatus)}
                            </span>
                          </div>
                        </div>

                        <div className="ftbi-buSummaryCol">
                          <div className="ftbi-buSummaryLabel">Plan</div>
                          <div className="ftbi-buSummaryValue">{bu.plan || "—"}</div>
                        </div>

                        <div className="ftbi-buSummaryCol">
                          <div className="ftbi-buSummaryLabel">Renews</div>
                          <div className="ftbi-buSummaryValue">{formatDate(bu.renewsAt)}</div>
                        </div>
                      </div>

                      <div className="ftbi-buSummaryArrow">{isExpanded ? "−" : "+"}</div>
                    </button>

                    {isExpanded ? (
                      <div className="ftbi-buExpanded">
                        <div className="ftbi-buDetailGrid">
                          <div className="ftbi-buDetailCard">
                            <div className="ftbi-buDetailLabel">Business Unit</div>
                            <div className="ftbi-buDetailValue">{formatUnitTitle(bu)}</div>
                            <div className="ftbi-buDetailSub">
                              {bu.storeCode || "No code"} · {bu.timezone} · {bu.status}
                            </div>
                          </div>

                          <div className="ftbi-buDetailCard">
                            <div className="ftbi-buDetailLabel">Plan</div>
                            <div className="ftbi-buDetailValue">{bu.plan || "—"}</div>
                            <div className="ftbi-buDetailSub">
                              {formatStatus(bu.subscriptionStatus)}
                            </div>
                          </div>

                          <div className="ftbi-buDetailCard">
                            <div className="ftbi-buDetailLabel">RO Cap</div>
                            <div className="ftbi-buDetailValue">{roCapLabel(bu.roCap)}</div>
                            <div className="ftbi-buDetailSub">Monthly allowance</div>
                          </div>

                          <div className="ftbi-buDetailCard">
                            <div className="ftbi-buDetailLabel">MRR</div>
                            <div className="ftbi-buDetailValue">{money(bu.mrr)}</div>
                            <div className="ftbi-buDetailSub">
                              Renews {formatDate(bu.renewsAt)}
                              {bu.cancelAtPeriodEnd ? " · canceling" : ""}
                            </div>
                          </div>
                        </div>

                        <div className="ftbi-buExpandedActions">
                          <button
                            className="ft-btn ft-btn--primary"
                            type="button"
                            onClick={() => handleCreatePortalLink(bu.id)}
                            disabled={!hasStripeCustomer || anyBusy}
                          >
                            {portalBusy ? "Creating…" : "Create Portal Link"}
                          </button>

                          {showStartStandard ? (
                            <button
                              className="ft-btn ft-btn--ghost"
                              type="button"
                              onClick={() => handleCreateCheckoutLink(bu.id, "standard")}
                              disabled={anyBusy}
                            >
                              {checkoutStandardBusy ? "Creating…" : "Create Starter Link"}
                            </button>
                          ) : null}

                          {showHighVolume ? (
                            <button
                              className="ft-btn ft-btn--ghost"
                              type="button"
                              onClick={() => handleCreateCheckoutLink(bu.id, "high_volume")}
                              disabled={anyBusy}
                            >
                              {checkoutHighBusy
                                ? "Creating…"
                                : onStarter
                                  ? "Create High Volume Link"
                                  : "Create High Volume Link"}
                            </button>
                          ) : null}

                          {!showStartStandard && onStarter ? (
                            <div className="ftbi-inlineFlag">Current Plan</div>
                          ) : null}

                          {onHighVolume ? (
                            <div className="ftbi-inlineFlag">Highest Plan</div>
                          ) : null}
                        </div>

                        <div className="ftbi-buExpandedHint">{rowHint}</div>

                        {starterLink || highVolumeLink || portalLink ? (
                          <div className="ftbi-linkBox">
                            <div className="ftbi-linkBoxTitle">Generated Links</div>

                            {starterLink ? (
                              <div className="ftbi-linkRow">
                                <div className="ftbi-linkLabel">Starter Checkout</div>
                                <input className="ftbi-linkInput" value={starterLink} readOnly />
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => handleCopySavedLink(bu.id, "standard")}
                                >
                                  {copiedKey === `${bu.id}:standard` ? "Copied" : "Copy"}
                                </button>
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => handleOpenSavedLink(bu.id, "standard")}
                                >
                                  Open
                                </button>
                              </div>
                            ) : null}

                            {highVolumeLink ? (
                              <div className="ftbi-linkRow">
                                <div className="ftbi-linkLabel">High Volume Checkout</div>
                                <input className="ftbi-linkInput" value={highVolumeLink} readOnly />
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => handleCopySavedLink(bu.id, "high_volume")}
                                >
                                  {copiedKey === `${bu.id}:high_volume` ? "Copied" : "Copy"}
                                </button>
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => handleOpenSavedLink(bu.id, "high_volume")}
                                >
                                  Open
                                </button>
                              </div>
                            ) : null}

                            {portalLink ? (
                              <div className="ftbi-linkRow">
                                <div className="ftbi-linkLabel">Billing Portal</div>
                                <input className="ftbi-linkInput" value={portalLink} readOnly />
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => handleCopySavedLink(bu.id, "portal")}
                                >
                                  {copiedKey === `${bu.id}:portal` ? "Copied" : "Copy"}
                                </button>
                                <button
                                  className="ft-btn ft-btn--ghost"
                                  type="button"
                                  onClick={() => handleOpenSavedLink(bu.id, "portal")}
                                >
                                  Open
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}